import sortBy from 'lodash/sortBy'
import moment from 'moment'

import type Company from 'modules/core/store/models/Company'

import { IncidenceQueryData } from 'modules/bookkeepers/hooks/useGetIncidences'
import type IncidenceChange from 'modules/bookkeepers/store/models/Change'
import { EMPLOYEE_TRACKED_CHANGES } from 'modules/bookkeepers/store/models/Incidence'
import { INCIDENCE_TYPES } from 'modules/payroll_bundle/lib/Incidences/Types'
import type { AvailableChange, IncidenceChangeItem } from 'modules/payroll_bundle/lib/types'

import { CHANGE_CREATED, getAvailableChanges, getModifiedValue, parseChange } from './changesHelper'

const processIncidenceChanges = (
  currentChange: IncidenceChange,
  previousChange: IncidenceChange | undefined,
  incidenceType: string,
  company: Company
) => {
  const changes: IncidenceChangeItem[] = []
  const availableChanges = getAvailableChanges(incidenceType)

  availableChanges.forEach((changeType: AvailableChange) => {
    const currentValue = getModifiedValue(
      changeType.field,
      currentChange.attributes,
      company,
      changeType.value_modifier
    )

    const previousValue = getModifiedValue(
      changeType.field,
      previousChange?.attributes,
      company,
      changeType.value_modifier
    )

    if (
      !EMPLOYEE_TRACKED_CHANGES[incidenceType] ||
      EMPLOYEE_TRACKED_CHANGES[incidenceType]?.includes(changeType.field)
    ) {
      const processedChange = parseChange(
        currentValue,
        previousValue,
        changeType,
        currentChange.attributes.effective_at,
        currentChange.attributes.author
      )

      if (processedChange) {
        changes.push(processedChange)
      }
    }
  })

  return changes
}

const sortByEffective = (collection: IncidenceChange[]) => {
  return sortBy(collection, (change) => change.get('effective_at'))
}

const isAffectedContract = (model: IncidenceChange, contractId: number | null) => {
  return (
    model.has('original_contract_version_id') &&
    model.get('original_contract_version_id') === contractId
  )
}

const sortChanges = (
  incidenceChanges: IncidenceChange[],
  incidenceType: string,
  incidenceTargetId: number | null
) => {
  if (incidenceType === INCIDENCE_TYPES.contract) {
    // Because the way Contract Versions work, we need the previous version and the current one. However, when
    // a change in a previous contract has happened, this can cause problems
    // For that reason, we sort it in two ways, first the affected contract (which is the one from the incidence)
    // and then the support contract, which is the contract with return that has no direct relation with the actual
    // contract
    let affectedContractChanges = incidenceChanges.filter((model) =>
      isAffectedContract(model, incidenceTargetId)
    )
    affectedContractChanges = sortByEffective(affectedContractChanges)

    let supportContractChange = incidenceChanges.filter(
      (model) => !isAffectedContract(model, incidenceTargetId)
    )
    supportContractChange = sortByEffective(supportContractChange)

    return [...supportContractChange, ...affectedContractChanges]
  }

  return sortByEffective(incidenceChanges)
}

const buildIncidenceChanges = (
  incidenceChanges: IncidenceChange[],
  incidenceType: string,
  company: Company,
  incidenceTargetId: number | null,
  incidence: IncidenceQueryData
) => {
  const sortedChanges = sortChanges(incidenceChanges, incidenceType, incidenceTargetId)

  return sortedChanges.flatMap((incidenceChange: IncidenceChange, index: number) => {
    if (index === 0) {
      const firstChangeId = CHANGE_CREATED

      const change: IncidenceChangeItem = {
        id: firstChangeId,
        effectiveAt: moment(incidence.createdAt),
      }

      return change
    }

    return processIncidenceChanges(
      incidenceChange,
      sortedChanges[index - 1],
      incidenceType,
      company
    )
  })
}

export default buildIncidenceChanges
